"use client";

import { useState } from "react";
import Link from "next/link";
import {
  Calculator,
  ArrowRight,
  Coins,
  Users,
  Info,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import {
  StructuredData,
  generateHowToSchema,
} from "@/components/seo/StructuredData";
import { RelatedLinks } from "@/components/seo/RelatedLinks";

const howToSchema = generateHowToSchema({
  title: "AGİ 2026 Güncel Durum ve Tarihsel Hesaplama",
  description:
    "2026'da AGİ ödemesi olup olmadığını ve tarihsel AGİ formülünü kontrol edin.",
  totalTime: "PT2M",
  steps: [
    {
      name: "Medeni Durumu Seçin",
      text: "Bekar, evli veya eşi çalışan/çalışmayan seçeneklerinden birini seçin.",
    },
    {
      name: "Çocuk Sayısını Girin",
      text: "Bakmakla yükümlü olduğunuz çocuk sayısını girin.",
    },
    {
      name: "Hesapla Butonuna Tıklayın",
      text: "AGİ tutarınızı görmek için 'Hesapla' butonuna tıklayın.",
    },
  ],
});

type MedeniDurum = "bekar" | "evli_esi_calisiyor" | "evli_esi_calismiyor";

interface AGIResult {
  medeniDurumOrani: number;
  cocukOrani: number;
  toplamOran: number;
  aylikAGI: number;
}

export default function AgiHesaplayiciClient() {
  const [medeniDurum, setMedeniDurum] = useState<MedeniDurum>("bekar");
  const [cocukSayisi, setCocukSayisi] = useState(0);
  const [result, setResult] = useState<AGIResult | null>(null);

  // AGİ parametreleri (7349 sayılı Kanun ile 01.01.2022'de kaldırıldı — referans amaçlı)
  const ASGARI_UCRET = 33_030; // 2026 brüt asgari ücret
  const GELIR_VERGISI_MATRAHI = ASGARI_UCRET * 12; // Yıllık brüt

  const calculateAGI = () => {
    // Medeni durum oranları
    let medeniDurumOrani = 0;
    switch (medeniDurum) {
      case "bekar":
        medeniDurumOrani = 50;
        break;
      case "evli_esi_calisiyor":
        medeniDurumOrani = 50;
        break;
      case "evli_esi_calismiyor":
        medeniDurumOrani = 60;
        break;
    }

    // Çocuk oranları
    let cocukOrani = 0;
    if (cocukSayisi >= 1) cocukOrani += 7.5;
    if (cocukSayisi >= 2) cocukOrani += 7.5;
    if (cocukSayisi >= 3) cocukOrani += 10;
    if (cocukSayisi >= 4) cocukOrani += (cocukSayisi - 3) * 5;

    // Toplam oran %100'ü geçemez
    const toplamOran = Math.min(medeniDurumOrani + cocukOrani, 100);

    // AGİ hesaplama
    // AGİ = Asgari ücret * Toplam oran * %15 (ilk dilim gelir vergisi oranı)
    const yillikAGI = GELIR_VERGISI_MATRAHI * (toplamOran / 100) * 0.15;
    const aylikAGI = yillikAGI / 12;

    setResult({
      medeniDurumOrani,
      cocukOrani,
      toplamOran,
      aylikAGI: Math.round(aylikAGI * 100) / 100,
    });
  };

  return (
    <>
      <StructuredData data={howToSchema} />

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-indigo-700 to-indigo-900 text-white">
          <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-10" />
          <div className="relative max-w-7xl mx-auto px-4 py-16 sm:py-20">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-white/20 rounded-xl">
                <Coins className="w-8 h-8" />
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold">
                AGİ 2026 Ne Kadar?
              </h1>
            </div>
            <p className="text-xl text-indigo-100 max-w-3xl">
              2026'da ayrı AGİ ödemesi yoktur. Güncel bordro etkisi asgari ücret
              gelir vergisi ve damga vergisi istisnası üzerinden
              değerlendirilir.
            </p>
          </div>
        </section>

        {/* Direct Answer */}
        <section className="max-w-4xl mx-auto px-4 pt-8">
          <div className="bg-white border border-indigo-100 rounded-2xl p-6 shadow-sm mb-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-sm font-semibold text-indigo-700 mb-4">
              <CheckCircle className="w-4 h-4" />
              Kısa cevap
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              2026'da AGİ tutarı ödenmez; güncel cevap 0 TL ayrı AGİ ödemesidir.
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Bekâr, evli veya çocuklu çalışan için 2026 bordrosunda ayrı bir
              AGİ satırı hesaplanmaz. Arama niyetinin güncel karşılığı, asgari
              ücretin gelir vergisi ve damga vergisi istisnasının net maaşa
              etkisini kontrol etmektir.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              <div className="rounded-xl bg-gray-50 p-4">
                <div className="text-sm text-gray-500">2026 AGİ ödemesi</div>
                <div className="text-xl font-bold text-gray-900">Yok</div>
              </div>
              <div className="rounded-xl bg-gray-50 p-4">
                <div className="text-sm text-gray-500">Ayrı AGİ tutarı</div>
                <div className="text-xl font-bold text-gray-900">0 TL</div>
              </div>
              <div className="rounded-xl bg-gray-50 p-4">
                <div className="text-sm text-gray-500">Güncel kontrol</div>
                <div className="text-xl font-bold text-gray-900">
                  Vergi istisnası
                </div>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-6 flex gap-4">
            <AlertTriangle className="w-7 h-7 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg font-bold text-amber-800 mb-2">
                AGİ 1 Ocak 2022 İtibarıyla Kaldırılmıştır
              </h2>
              <p className="text-amber-700 leading-relaxed">
                7349 sayılı Kanun ile{" "}
                <strong>Asgari Geçim İndirimi (AGİ)</strong> uygulaması 1 Ocak
                2022 tarihinden itibaren yürürlükten kaldırılmıştır. Yerine,
                asgari ücrete kadar olan kısım gelir vergisi ve damga
                vergisinden istisna tutulmaktadır. Aşağıdaki hesaplayıcı
                yalnızca <strong>referans amaçlıdır</strong>.
              </p>
            </div>
          </div>
        </section>

        {/* Calculator */}
        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <Calculator className="w-6 h-6 text-indigo-600" />
              <h2 className="text-2xl font-bold text-gray-900">Hesaplama</h2>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Medeni Durum *
                </label>
                <div className="grid sm:grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setMedeniDurum("bekar")}
                    className={`p-4 rounded-lg border-2 text-center transition-all ${
                      medeniDurum === "bekar"
                        ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <Users className="w-5 h-5 mx-auto mb-2" />
                    <div className="font-medium">Bekâr</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMedeniDurum("evli_esi_calisiyor")}
                    className={`p-4 rounded-lg border-2 text-center transition-all ${
                      medeniDurum === "evli_esi_calisiyor"
                        ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <Users className="w-5 h-5 mx-auto mb-2" />
                    <div className="font-medium">Evli</div>
                    <div className="text-xs text-gray-500">Eşi çalışıyor</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMedeniDurum("evli_esi_calismiyor")}
                    className={`p-4 rounded-lg border-2 text-center transition-all ${
                      medeniDurum === "evli_esi_calismiyor"
                        ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <Users className="w-5 h-5 mx-auto mb-2" />
                    <div className="font-medium">Evli</div>
                    <div className="text-xs text-gray-500">Eşi çalışmıyor</div>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Çocuk Sayısı
                </label>
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => setCocukSayisi(Math.max(0, cocukSayisi - 1))}
                    className="w-12 h-12 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center justify-center text-2xl"
                  >
                    -
                  </button>
                  <span className="text-3xl font-bold text-gray-900 w-16 text-center">
                    {cocukSayisi}
                  </span>
                  <button
                    type="button"
                    onClick={() => setCocukSayisi(cocukSayisi + 1)}
                    className="w-12 h-12 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center justify-center text-2xl"
                  >
                    +
                  </button>
                </div>
              </div>

              <button
                onClick={calculateAGI}
                className="w-full bg-indigo-600 text-white py-4 rounded-xl font-semibold hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
              >
                <Calculator className="w-5 h-5" />
                Hesapla
              </button>
            </div>

            {/* Result */}
            {result && (
              <div className="mt-8 p-6 bg-indigo-50 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Hesaplama Sonucu
                </h3>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div className="bg-white rounded-lg p-4">
                    <div className="text-sm text-gray-600">
                      Medeni Durum Oranı
                    </div>
                    <div className="text-xl font-bold text-gray-900">
                      %{result.medeniDurumOrani}
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <div className="text-sm text-gray-600">Çocuk Oranı</div>
                    <div className="text-xl font-bold text-gray-900">
                      %{result.cocukOrani}
                    </div>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4">
                    <div className="text-sm text-gray-600">
                      Toplam AGİ Oranı
                    </div>
                    <div className="text-xl font-bold text-indigo-600">
                      %{result.toplamOran}
                    </div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600">
                      Aylık AGİ Tutarı
                    </div>
                    <div className="text-2xl font-bold text-green-600">
                      {result.aylikAGI.toLocaleString("tr-TR", {
                        minimumFractionDigits: 2,
                      })}{" "}
                      ₺
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Detailed Content */}
        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="prose prose-indigo max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              AGİ ve 2026 Vergi İstisnası Rehberi
            </h2>

            <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                AGİ Nedir?
              </h3>
              <p className="text-gray-700 mb-4">
                Asgari Geçim İndirimi (AGİ), 2022 öncesinde çalışanların medeni
                durumu ve çocuk sayısına göre uygulanan gelir vergisi indirimi
                sistemiydi. 1 Ocak 2022 itibarıyla kaldırıldı. 2026 yılında AGİ
                uygulanmaz; bunun yerine asgari ücret vergi istisnası sistemi
                kullanılır. Bu sayfadaki hesaplayıcı, geçmiş dönem mantığını
                anlamak isteyen ekipler için referans amaçlı tutulur.
              </p>

              <div className="bg-indigo-50 rounded-xl p-6 mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">
                  Tarihsel AGİ Oranları Tablosu
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="py-3 px-4 font-semibold text-gray-900">
                          Durum
                        </th>
                        <th className="py-3 px-4 font-semibold text-gray-900">
                          Oran
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="py-3 px-4 text-gray-700">Bekâr</td>
                        <td className="py-3 px-4 text-gray-900 font-medium">
                          %50
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-700">
                          Evli (eşi çalışıyor)
                        </td>
                        <td className="py-3 px-4 text-gray-900 font-medium">
                          %50
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-700">
                          Evli (eşi çalışmıyor)
                        </td>
                        <td className="py-3 px-4 text-gray-900 font-medium">
                          %60
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-700">1. Çocuk</td>
                        <td className="py-3 px-4 text-gray-900 font-medium">
                          +%7.5
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-700">2. Çocuk</td>
                        <td className="py-3 px-4 text-gray-900 font-medium">
                          +%7.5
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-700">3. Çocuk</td>
                        <td className="py-3 px-4 text-gray-900 font-medium">
                          +%10
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-700">
                          4. ve sonraki çocuklar
                        </td>
                        <td className="py-3 px-4 text-gray-900 font-medium">
                          Her biri için +%5
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <h3 className="text-2xl font-semibold text-gray-900 mb-4 mt-8">
                Tarihsel Örnek Hesaplama Senaryosu
              </h3>
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <p className="text-gray-700 mb-4">
                  <strong>Senaryo:</strong> Ali Bey, evli ve eşi çalışmıyor. 2
                  çocuğu var. Bu örnek, eski AGİ mantığının nasıl işlediğini
                  göstermek içindir; 2026 yılında doğrudan uygulanmaz.
                </p>
                <div className="space-y-2 text-gray-700">
                  <p>
                    <strong>1. Medeni Durum Oranı:</strong>
                  </p>
                  <p className="ml-4">Evli (eşi çalışmıyor): %60</p>

                  <p className="mt-4">
                    <strong>2. Çocuk Oranı:</strong>
                  </p>
                  <p className="ml-4">1. çocuk: +%7.5</p>
                  <p className="ml-4">2. çocuk: +%7.5</p>
                  <p className="ml-4">Toplam çocuk oranı: %15</p>

                  <p className="mt-4">
                    <strong>3. Toplam AGİ Oranı:</strong>
                  </p>
                  <p className="ml-4">Medeni durum: %60</p>
                  <p className="ml-4">Çocuk: %15</p>
                  <p className="ml-4">Toplam: %75 (maksimum %100)</p>

                  <p className="mt-4">
                    <strong>4. Tarihsel AGİ Formülü:</strong>
                  </p>
                  <p className="ml-4">
                    Buradaki örnek, güncel ödeme değil referans formüldür.
                  </p>
                  <p className="ml-4">
                    AGİ döneminde yıllık asgari ücret matrahı esas alınırdı.
                  </p>
                  <p className="ml-4">
                    Formül:{" "}
                    <strong>
                      (Asgari ücret × 12) × oran × ilk vergi dilimi
                    </strong>
                  </p>
                </div>
              </div>

              <h3 className="text-2xl font-semibold text-gray-900 mb-4 mt-8">
                2026'da Ne Geçerli?
              </h3>
              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <p className="text-gray-700 mb-4">
                  <strong>2026 gerçek durum:</strong> AGİ ödemesi yapılmaz.
                  Bordroda çalışan lehine etki yaratan unsur, asgari ücret vergi
                  istisnasıdır. Bu yüzden ekiplerin aradığı cevap artık "AGİ ne
                  kadar?" değil, "vergi istisnası bordroya nasıl yansıyor?"
                  sorusudur.
                </p>
                <ul className="text-gray-700 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-blue-600">
                      Bordro ekibi için doğru kontrol:
                    </span>
                    <span>
                      Gelir vergisi, damga vergisi ve istisna satırlarını
                      birlikte okuyun.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-blue-600">
                      2026 arama niyeti:
                    </span>
                    <span>
                      "2026 AGİ ne kadar" araması aslında vergi istisnası ve net
                      ücret etkisini anlamaya çalışır.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-blue-600">
                      Operasyon önerisi:
                    </span>
                    <span>
                      Net ücret etkisini görmek için maaş hesaplayıcı ve bordro
                      örneği sayfalarıyla birlikte değerlendirme yapın.
                    </span>
                  </li>
                </ul>
              </div>

              <h3 className="text-2xl font-semibold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h3>
              <div className="space-y-4">
                <div className="border border-gray-200 rounded-xl p-6">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    AGİ nedir ve kimler yararlanır?
                  </h4>
                  <p className="text-gray-700">
                    AGİ, 2022 öncesi gelir vergisi indirimi sistemiydi. 2026
                    yılında çalışanlar AGİ almaz; bunun yerine asgari ücret
                    vergi istisnası uygulanır.
                  </p>
                </div>

                <div className="border border-gray-200 rounded-xl p-6">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    AGİ nasıl hesaplanır?
                  </h4>
                  <p className="text-gray-700">
                    Tarihsel olarak AGİ, yıllık asgari ücret matrahı, aile
                    durumu oranı ve ilk gelir vergisi dilimi üzerinden
                    hesaplanırdı. Bugün bordro ekiplerinin bakması gereken şey
                    bu formül değil, vergi istisnasının net ücrete etkisidir.
                  </p>
                </div>

                <div className="border border-gray-200 rounded-xl p-6">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    AGİ oranı nasıl belirlenir?
                  </h4>
                  <p className="text-gray-700">
                    Tarihsel AGİ oranı medeni durum ve çocuk sayısına göre
                    belirlenirdi. Bu sayfadaki oranlar geçmiş sistemin mantığını
                    göstermek içindir; 2026 bordro hesaplamasında doğrudan ödeme
                    anlamına gelmez.
                  </p>
                </div>

                <div className="border border-gray-200 rounded-xl p-6">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    AGİ net maaşı nasıl etkiler?
                  </h4>
                  <p className="text-gray-700">
                    AGİ döneminde gelir vergisi azalttığı için net maaşı
                    artırırdı. Güncel sistemde benzer etki vergi istisnası
                    üzerinden görülür; bu yüzden net maaş etkisini güncel bordro
                    ve maaş hesaplama araçlarıyla okumak gerekir.
                  </p>
                </div>

                <div className="border border-gray-200 rounded-xl p-6">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    AGİ her ay değişir mi?
                  </h4>
                  <p className="text-gray-700">
                    AGİ bugün aktif bir uygulama olmadığı için aylık AGİ
                    değişiminden söz edilmez. Güncel takip edilmesi gereken
                    başlıklar; asgari ücret, vergi dilimleri ve bordro
                    parametreleridir.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Info */}
        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-gray-50 rounded-2xl p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-4">
              <Info className="w-5 h-5 text-indigo-600" />
              <h2 className="text-xl font-bold text-gray-900">
                Tarihsel AGİ Oranları
              </h2>
            </div>

            <div className="overflow-x-auto mb-6">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="py-3 px-4 font-semibold text-gray-900">
                      Durum
                    </th>
                    <th className="py-3 px-4 font-semibold text-gray-900">
                      Oran
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="py-3 px-4 text-gray-700">Bekâr</td>
                    <td className="py-3 px-4 text-gray-900 font-medium">%50</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-700">
                      Evli (eşi çalışıyor)
                    </td>
                    <td className="py-3 px-4 text-gray-900 font-medium">%50</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-700">
                      Evli (eşi çalışmıyor)
                    </td>
                    <td className="py-3 px-4 text-gray-900 font-medium">%60</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-700">1. Çocuk</td>
                    <td className="py-3 px-4 text-gray-900 font-medium">
                      +%7.5
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-700">2. Çocuk</td>
                    <td className="py-3 px-4 text-gray-900 font-medium">
                      +%7.5
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-700">3. Çocuk</td>
                    <td className="py-3 px-4 text-gray-900 font-medium">
                      +%10
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-700">
                      4. ve sonraki çocuklar
                    </td>
                    <td className="py-3 px-4 text-gray-900 font-medium">
                      Her biri için +%5
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="prose prose-gray max-w-none">
              <h3 className="text-lg font-semibold">AGİ Nedir?</h3>
              <p className="text-gray-700">
                Asgari Geçim İndirimi (AGİ), bugün aktif olmayan tarihsel bir
                vergi indirimi modelidir. 2026 itibarıyla bordro ekiplerinin
                odaklanması gereken şey, asgari ücret vergi istisnası ve net
                ücret etkisidir.
              </p>
              <p className="text-sm text-gray-500 mt-4">
                Not: AGİ 7349 sayılı Kanun ile 01.01.2022 tarihinde
                kaldırılmıştır. Bu hesaplama referans amaçlıdır; güncel bordro
                yorumu için vergi istisnası yaklaşımı esas alınmalıdır.
              </p>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-4">
          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-100 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              2026 AGİ Sorgusunun GEO Karşılığı
            </h2>
            <p className="text-gray-700 mb-4">
              İstanbul, Ankara, İzmir, Bursa ve Antalya'daki bordro ekipleri
              hâlâ "2026 AGİ ne kadar?" sorusunu soruyor; çünkü kullanıcı niyeti
              çoğu zaman güncel vergi istisnasının net maaşa etkisini öğrenmek
              oluyor. Bu nedenle AGİ sorgusu, fiilen bordro ve maaş hesaplama
              niyetine bağlanıyor.
            </p>
            <p className="text-gray-700">
              Özellikle çok lokasyonlu şirketlerde, aynı kavramın eski adıyla
              aranması operasyon ekiplerinde bilgi karışıklığı yaratabiliyor. Bu
              araç bu karışıklığı açıklamak için referans katmanı, IKAI'nin
              bordro ve maaş hesaplama sayfaları ise güncel uygulama katmanı
              olarak düşünülmelidir.
            </p>
          </div>
        </section>

        {/* Related Links */}
        <section className="max-w-7xl mx-auto px-4 py-8">
          <RelatedLinks
            pageType="tool"
            currentSlug="agi-hesaplayici"
            title="İlgili Sayfalar"
            maxLinks={4}
          />
        </section>

        {/* CTA */}
        <section className="max-w-7xl mx-auto px-4 py-12">
          <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 rounded-3xl p-8 sm:p-12 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">
              Bordro Hesaplamalarını Otomatikleştirin
            </h2>
            <p className="text-lg text-indigo-100 mb-8 max-w-2xl mx-auto">
              IKAI ile AGİ, SGK, vergi hesaplamaları ve tüm bordro süreçlerinizi
              otomatik yönetin.
            </p>
            <Link
              href="/register"
              className="inline-flex items-center justify-center gap-2 bg-white text-indigo-700 px-8 py-4 rounded-xl font-semibold hover:bg-indigo-50 transition-colors"
            >
              Ücretsiz Başlayın
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
